export interface MountFormerieEmbedOptions {
  document?: Document;
  endpoint?: string;
  form?: string;
  height?: string;
  script?: HTMLScriptElement | null;
  target?: Element | string | null;
  title?: string;
}

export interface MountedFormerieEmbed {
  destroy(): void;
  element: Element;
  iframe: HTMLIFrameElement;
}

export function buildFormerieEmbedIframeUrl(endpoint: string, form?: string): string {
  const path = form?.trim()
    ? `/${form.trim().replace(/^\/+|\/+$/g, "")}`
    : "/";

  return new URL(path, endpoint).toString();
}

export function mountFormerieEmbed(
  options: MountFormerieEmbedOptions = {},
): MountedFormerieEmbed | null {
  const script = options.script ?? currentScript();
  const documentRef = options.document ?? script?.ownerDocument ?? currentDocument();
  if (!documentRef) {
    return null;
  }

  const element = resolveEmbedTarget(documentRef, options.target ?? script?.dataset.formerieTarget ?? script?.parentElement);
  if (!element) {
    return null;
  }

  const endpoint = options.endpoint ?? script?.dataset.formerieEndpoint ?? scriptEndpoint(script);
  if (!endpoint) {
    return null;
  }

  const iframe = documentRef.createElement("iframe");
  iframe.src = buildFormerieEmbedIframeUrl(endpoint, options.form ?? script?.dataset.formerieForm);
  iframe.title = options.title ?? script?.dataset.formerieTitle ?? "Formerie form";
  iframe.loading = "lazy";
  iframe.style.width = "100%";
  iframe.style.border = "0";
  iframe.style.minHeight = options.height ?? script?.dataset.formerieHeight ?? "720px";
  element.appendChild(iframe);

  return {
    destroy() {
      iframe.remove();
    },
    element,
    iframe,
  };
}

function currentScript(): HTMLScriptElement | null {
  const documentRef = currentDocument();
  return typeof HTMLScriptElement !== "undefined" && documentRef?.currentScript instanceof HTMLScriptElement
    ? documentRef.currentScript
    : null;
}

function currentDocument(): Document | undefined {
  return typeof document === "undefined" ? undefined : document;
}

function resolveEmbedTarget(
  documentRef: Document,
  target: Element | string | null | undefined,
): Element | null {
  if (typeof target === "string") {
    return documentRef.querySelector(target);
  }

  return target ?? null;
}

function scriptEndpoint(script: HTMLScriptElement | null | undefined): string | undefined {
  if (!script?.src) {
    return undefined;
  }

  return new URL(script.src).origin;
}
